import React from 'react';
import { Box, List, ListItemButton, Stack, Typography, alpha } from '@mui/material';
import { fmtPercent } from './formatters';
import StatusChip, { STATUS_COLORS } from './StatusChip';
import type { ChainEntitySummary } from './types';

export interface EntityListPanelProps {
  title: string;
  entities: ChainEntitySummary[];
  selectedEntityId: string | null;
  onSelectEntity: (entityId: string) => void;
  /** Which metric (by index into `entity.metrics`) to show as the row's delta. Defaults to 0. */
  highlightMetricIndex?: number;
  emptyMessage?: string;
  /** Cap the list's visible height to roughly this many rows and scroll the
   * rest, instead of letting the panel grow with the entity count (e.g. a
   * 50-broker list would otherwise push the page far past the fold). */
  maxVisibleItems?: number;
}

// Approximate rendered height of one row (py:0.75 padding + two text lines +
// 1px border) plus the list's inter-row gap — used only to size the
// scroll viewport for `maxVisibleItems`, not for exact pixel alignment.
const ROW_HEIGHT_PX = 52;
const ROW_GAP_PX = 4;

/** Generic entity picker list — "click into a declining broker" for Broker
 * Monitor today; any future monitor with a rankable list of sub-entities
 * (deals, campaigns, accounts, ...) can reuse this. */
export default function EntityListPanel({
  title,
  entities,
  selectedEntityId,
  onSelectEntity,
  highlightMetricIndex = 0,
  emptyMessage = 'No entities to show.',
  maxVisibleItems,
}: EntityListPanelProps) {
  const scrollSx = maxVisibleItems
    ? {
        maxHeight: maxVisibleItems * ROW_HEIGHT_PX + (maxVisibleItems - 1) * ROW_GAP_PX,
        overflowY: 'auto' as const,
        overflowX: 'hidden' as const,
        pr: 0.5,
      }
    : undefined;
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>
      {entities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      ) : (
        <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ...scrollSx }}>
          {entities.map((entity) => {
            const selected = entity.entity_id === selectedEntityId;
            const metric = entity.metrics[highlightMetricIndex];
            const color = STATUS_COLORS[entity.status];
            return (
              <ListItemButton
                key={entity.entity_id}
                selected={selected}
                onClick={() => onSelectEntity(entity.entity_id)}
                sx={(t) => ({
                  borderRadius: `${t.ds?.radius?.sm ?? 6}px`,
                  border: '1px solid',
                  borderColor: selected ? t.palette.primary.main : t.tokens.border.subtle,
                  bgcolor: selected ? alpha(t.palette.primary.main, 0.06) : undefined,
                  py: 0.75,
                  px: 1,
                })}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0, mr: 1 }} />
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {entity.entity_name}
                  </Typography>
                  {entity.subtitle && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {entity.subtitle}
                    </Typography>
                  )}
                </Stack>
                <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0, ml: 1 }}>
                  <StatusChip status={entity.status} label={entity.label} />
                  {metric && metric.pct_change != null && (
                    <Typography variant="caption" fontWeight={700} color={metric.sentiment === 'bad' ? 'error.main' : metric.sentiment === 'good' ? 'success.main' : 'text.secondary'}>
                      {fmtPercent(metric.pct_change, true)}
                    </Typography>
                  )}
                </Stack>
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
}
